import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { ToeBoard, UpdateToeBoardBody } from "../../../../../http/border/types/toeBoard";
import { useToeBoardExtrasApi } from "../../../../../http/border/useToeBoardExtras";
import { useToeBoardsApi } from "../../../../../http/border/useToeBoards";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface ToeBoardFormBody {
  item: SearchAdminItem | null;
  length: number;
  toeBoardExtraIds: string[];
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface ToeBoardFormProps {
  toeBoard?: ToeBoard;
  onClose: () => void;
}

const ToeBoardForm: FC<ToeBoardFormProps> = ({ onClose, toeBoard }) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const itemsApi = useItemsApi();
  const toeBoardsApi = useToeBoardsApi();
  const toeBoardExtrasApi = useToeBoardExtrasApi();
  const toeBoardExtras = toeBoardExtrasApi.useAdminToeBoardExtras({ limit: 100 }, aborter.current.signal);

  const defaultValues = {
    item: toeBoard?.item ?? null,
    length: toeBoard?.length,
    toeBoardExtraIds: toeBoard?.extras?.map((x) => x.item.id) ?? [],
    order: toeBoard?.order ?? 50,
    visibility: toeBoard?.visibility ?? "hidden",
  };

  return (
    <Form<ToeBoardFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        const common: UpdateToeBoardBody = {
          length: formData.length,
          toeBoardExtraIds: formData.toeBoardExtraIds,
          order: formData.order!,
          visibility: formData.visibility,
        };
        if (toeBoard)
          try {
            await toeBoardsApi.updateAdminToeBoard(toeBoard.item.id, common);
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        else
          try {
            await toeBoardsApi.insertAdminToeBoard({
              ...common,
              itemId: formData.item?.id ?? "",
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
      }}
    >
      {({ control, setValue, reset, watch }) => {
        useEffect(() => {
          defaultValues && reset(defaultValues);
        }, [toeBoard]);

        const toeBoardExtraIds = watch("toeBoardExtraIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={itemLabel}
                            isDisabled={!!toeBoard?.item.id}
                            getOptionValue={(x) => x.id}
                            isInvalid={fieldState.invalid}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="length"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.length} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      suffix=" mm"
                      allowNegative={false}
                      onBlur={(value) => value && setValue("length", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.toeBoardExtras.plural}>
              <MultiCheckbox
                disabled={isSubmitting}
                values={
                  toeBoardExtras.data?.items.map((x) => ({
                    id: x.item.id,
                    title: itemLabel(x.item),
                    selected: toeBoardExtraIds.includes(x.item.id),
                  })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue(
                    "toeBoardExtraIds",
                    checked ? [...toeBoardExtraIds, id] : toeBoardExtraIds.filter((x) => x !== id),
                  );
                }}
              />
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default ToeBoardForm;
